/**
 * The item catalogue: every cosmetic the game ships, as data.
 *
 * ===========================================================================
 * AN ITEM IS A RECIPE, NEVER A FILE
 * ===========================================================================
 *
 * There are no asset files in this project (see the header of ./cosmetics), so
 * every entry here is a `generator` name plus `params`. The renderer looks the
 * generator up in a table of procedural draw functions and feeds it the
 * parameters; nothing in this file knows how a hex weave or a Nordic cross is
 * drawn, and nothing in the renderer knows which items exist.
 *
 * THE TWO HALVES MEET AT THE GENERATOR NAME, so the names here are not free.
 * Every one in a slot the renderer draws is a key of one of its registries —
 * `DECAL_GENERATORS`, `FLAG_GENERATORS` and `NUMBER_GENERATORS` in
 * src/render/decals.ts, `RIM_GENERATORS`, `BOOST_GENERATORS`,
 * `TRAIL_GENERATORS`, `TOPPER_GENERATORS` and `ANTENNA_GENERATORS` in
 * src/render/livery.ts, `PAINT_FINISHES` in src/render/materials.ts, and
 * `CAR_BODY_IDS` for the body slot. A name that is not one of those is an item
 * that equips and draws NOTHING, with no error anywhere — which is why the
 * renderer publishes `missingGenerators` and asserts it is empty. The params
 * are equally not free: each generator reads specific keys, and a key it does
 * not read is a number in a table that changes nothing.
 *
 * The five slots the renderer does not draw — goal explosions, engine audio,
 * banners, titles, avatar borders — name generators owned by this file, in the
 * same camelCase style, and are documented alongside the rest in
 * docs/COSMETICS_SOURCES.md. A test asserts the documented set and the used set
 * are identical, in both directions.
 *
 * ===========================================================================
 * PAINTABLE AND CERTIFIABLE ARE DERIVED, NOT TYPED IN
 * ===========================================================================
 *
 * In the real game a painted or certified variant starts at Rare — there is no
 * Titanium White Common. That is a rule, so it is written once as `isPaintable`
 * / `isCertifiable` and applied by the builder, rather than being two booleans
 * per item that three hundred rows of table could get wrong. The one exception
 * is the `number` slot, which is ours: every number is paintable, because a
 * racing number you cannot colour is a racing number nobody wants.
 *
 * COLOUR CONVENTION. Every colour here is a 0xRRGGBB number, which is what the
 * renderer's `hex`/`hexParam` readers expect and what three.js `Color` takes.
 * Loadout colours are '#rrggbb' STRINGS because `Loadout` in ./cosmetics says
 * so; `paintHexString` is the one conversion.
 */

import type { CarBody } from '../physics/car-config';
import {
  PAINT_COLORS,
  RARITY_ORDER,
  type CosmeticSlot,
  type ItemDef,
  type PaintColor,
  type Rarity,
} from './cosmetics';

// ---------------------------------------------------------------------------
// Rarity and paint tables
// ---------------------------------------------------------------------------

const RARITY_INDEX: Readonly<Record<Rarity, number>> = Object.freeze(
  Object.fromEntries(RARITY_ORDER.map((r, i) => [r, i])) as Record<Rarity, number>
);

/** Position on the ladder. `common` is 0; higher is rarer. */
export const rarityRank = (r: Rarity): number => RARITY_INDEX[r] ?? 0;

/** The rarity one step up, or null at the top of the ladder. */
export const nextRarity = (r: Rarity): Rarity | null => RARITY_ORDER[rarityRank(r) + 1] ?? null;

/**
 * UI colour per rarity. Approximates Rocket League's own ramp — grey, blue,
 * purple, pink, gold, crimson — so a player who knows that game reads this one
 * without a legend.
 */
export const RARITY_HEX: Readonly<Record<Rarity, number>> = Object.freeze({
  common: 0xb9c0c8,
  uncommon: 0x4f9cf0,
  rare: 0x2f6fe0,
  veryRare: 0x8b5cf6,
  import: 0xe0417b,
  exotic: 0xf2c14e,
  blackMarket: 0xd0362a,
  limited: 0xff8c1a,
});

/**
 * The thirteen painted variants as colours. Eyeballed against the real game's
 * palette rather than sampled from it — these are the colours the finishes are
 * NAMED for, and the names are the part that is copied. Titanium White is a
 * touch brighter and cooler than White, which is the whole reason both exist.
 */
export const PAINT_HEX: Readonly<Record<PaintColor, number>> = Object.freeze({
  crimson: 0xb1231f,
  skyBlue: 0x7ec8e3,
  pink: 0xff9fd0,
  cobalt: 0x2a4fd6,
  burntSienna: 0x8a4b2a,
  forestGreen: 0x1f6b3a,
  purple: 0x6b2fbf,
  saffron: 0xf5d54a,
  lime: 0xa8e024,
  grey: 0x6e7276,
  black: 0x17181a,
  white: 0xd8dce0,
  titaniumWhite: 0xf7f9fb,
});

// ---------------------------------------------------------------------------
// Which slots carry which attributes
// ---------------------------------------------------------------------------

/** Slots whose items can roll a painted variant. `number` is ours. */
export const PAINTABLE_SLOTS: readonly CosmeticSlot[] = [
  'decal', 'wheels', 'boost', 'topper', 'antenna', 'trail', 'goalExplosion', 'number',
];

/** Slots whose items can roll a certification. */
export const CERTIFIABLE_SLOTS: readonly CosmeticSlot[] = [
  'decal', 'wheels', 'boost', 'topper', 'antenna', 'trail', 'goalExplosion', 'number',
];

/** Painted and certified variants start here, as they do in the real game. */
export const ATTRIBUTE_MIN_RARITY: Rarity = 'rare';

export const isPaintable = (slot: CosmeticSlot, rarity: Rarity): boolean =>
  PAINTABLE_SLOTS.includes(slot) &&
  // Ours, and exempt: see the header.
  (slot === 'number' || rarityRank(rarity) >= rarityRank(ATTRIBUTE_MIN_RARITY));

export const isCertifiable = (slot: CosmeticSlot, rarity: Rarity): boolean =>
  CERTIFIABLE_SLOTS.includes(slot) && rarityRank(rarity) >= rarityRank(ATTRIBUTE_MIN_RARITY);

// ---------------------------------------------------------------------------
// The builder
// ---------------------------------------------------------------------------

type Params = Readonly<Record<string, number | string | boolean>>;

interface Extra {
  bodies?: readonly CarBody[];
  exclusive?: boolean;
}

const def = (
  slot: CosmeticSlot,
  id: string,
  name: string,
  rarity: Rarity,
  generator: string,
  params: Params = {},
  extra: Extra = {}
): ItemDef =>
  Object.freeze({
    id,
    slot,
    name,
    rarity,
    ...(extra.bodies ? { bodies: Object.freeze([...extra.bodies]) } : {}),
    paintable: isPaintable(slot, rarity),
    certifiable: isCertifiable(slot, rarity),
    generator,
    params: Object.freeze({ ...params }),
    ...(extra.exclusive ? { exclusive: true } : {}),
  });

/** Curries the slot, so a table row reads `id, name, rarity, generator, params`. */
const maker =
  (slot: CosmeticSlot) =>
  (
    id: string,
    name: string,
    rarity: Rarity,
    generator: string,
    params: Params = {},
    extra: Extra = {}
  ): ItemDef =>
    def(slot, id, name, rarity, generator, params, extra);

const decal = maker('decal');
const finish = maker('paintFinish');
const wheels = maker('wheels');
const boost = maker('boost');
const topper = maker('topper');
const antenna = maker('antenna');
const trail = maker('trail');
const goalExplosion = maker('goalExplosion');
const engineAudio = maker('engineAudio');
const banner = maker('banner');
const title = maker('title');
const avatarBorder = maker('avatarBorder');

// ---------------------------------------------------------------------------
// Bodies
// ---------------------------------------------------------------------------

/**
 * The bodies, one item each, `generator` being the body id itself — that is the
 * convention `resolveLivery` reads, and it keeps the geometry in
 * src/render/car-body.ts and the physics in src/physics/car-config.ts rather
 * than restating either here.
 *
 * TWO, not the seven `CarBody` names. RocketSim publishes hitboxes for seven
 * presets and the simulation supports all of them, but only the Octane and the
 * Dominus have a MODEL, so only those two are things a player can select
 * without the car turning into a hole on the pitch. When a third body is lofted
 * it becomes a third row here and nothing else changes.
 */
const RENDERED_BODIES: readonly CarBody[] = ['octane', 'dominus'];

const BODIES: readonly ItemDef[] = [
  def('body', 'body-octane', 'Octane', 'common', 'octane', {}, { bodies: ['octane'] }),
  def('body', 'body-dominus', 'Dominus', 'common', 'dominus', {}, { bodies: ['dominus'] }),
];

// ---------------------------------------------------------------------------
// Decals
// ---------------------------------------------------------------------------

const DECALS: readonly ItemDef[] = [
  decal('dc-plain', 'Stock', 'common', 'plain'),
  decal('dc-stripes-twin', 'Racing Stripes', 'common', 'stripes', { count: 2, width: 0.09, gap: 0.06, pinstripe: true }),
  decal('dc-stripes-solo', 'Centre Stripe', 'common', 'stripes', { count: 1, width: 0.16, gap: 0, pinstripe: false }),
  decal('dc-stripes-quad', 'Quad Stripe', 'uncommon', 'stripes', { count: 4, width: 0.05, gap: 0.04, pinstripe: true }),
  decal('dc-stripes-six', 'Sixpack', 'rare', 'stripes', { count: 6, width: 0.035, gap: 0.03, pinstripe: false }),
  decal('dc-belt', 'Beltline', 'common', 'beltStripe', { at: 0.62, width: 0.085, taper: 0.55 }),
  decal('dc-belt-wide', 'Broadside', 'uncommon', 'beltStripe', { at: 0.58, width: 0.16, taper: 0.35 }),
  decal('dc-belt-taper', 'Speedline', 'rare', 'beltStripe', { at: 0.66, width: 0.06, taper: 0.85 }),
  decal('dc-pin', 'Pinstripe', 'common', 'pinstripe', { at: 0.72, width: 0.016, spacing: 0.032 }),
  decal('dc-pin-wide', 'Coachline', 'uncommon', 'pinstripe', { at: 0.68, width: 0.026, spacing: 0.075 }),
  decal('dc-flames', 'Flamethrower', 'rare', 'flames', { reach: 0.52, tongues: 7, roughness: 0.32 }),
  decal('dc-flames-inferno', 'Inferno', 'veryRare', 'flames', { reach: 0.75, tongues: 11, roughness: 0.55 }),
  decal('dc-flames-wisp', 'Wisp', 'import', 'flames', { reach: 0.4, tongues: 4, roughness: 0.15 }),
  decal('dc-chevron', 'Chevron', 'common', 'chevron', { count: 4, width: 0.05, rake: 0.55, centre: 0.62 }),
  decal('dc-chevron-tight', 'Arrowhead', 'uncommon', 'chevron', { count: 8, width: 0.03, rake: 0.9, centre: 0.6 }),
  decal('dc-chevron-bold', 'Spearhead', 'rare', 'chevron', { count: 2, width: 0.11, rake: 0.35, centre: 0.64 }),
  decal('dc-hex', 'Hex Weave', 'uncommon', 'hex', { scale: 16, line: 0.14, fade: true }),
  decal('dc-hex-bold', 'Hexforge', 'rare', 'hex', { scale: 8, line: 0.28, fade: false }),
  decal('dc-camo', 'Woodland', 'uncommon', 'camo', { scale: 7, coverage: 0.46 }),
  decal('dc-camo-fine', 'Digital Camo', 'rare', 'camo', { scale: 14, coverage: 0.38 }),
  decal('dc-camo-arctic', 'Arctic Camo', 'rare', 'camo', { scale: 4, coverage: 0.6 }),
  decal('dc-wave', 'Tidal', 'rare', 'wave', { amplitude: 0.14, width: 0.16, cycles: 1.15, centre: 0.62 }),
  decal('dc-wave-fast', 'Ripcurl', 'veryRare', 'wave', { amplitude: 0.22, width: 0.1, cycles: 2.6, centre: 0.6 }),
  decal('dc-dots', 'Halftone', 'uncommon', 'dots', { scale: 22, grow: true, radius: 0.42 }),
  decal('dc-dots-dense', 'Dot Matrix', 'rare', 'dots', { scale: 40, grow: false, radius: 0.3 }),
  decal('dc-grid', 'Blueprint', 'uncommon', 'grid', { along: 18, around: 10, line: 0.055 }),
  decal('dc-grid-fine', 'Circuitry', 'veryRare', 'grid', { along: 40, around: 22, line: 0.03 }),
  decal('dc-shards', 'Shatter', 'veryRare', 'shards', { count: 6, rake: 1.35, start: 0.42 }),
  decal('dc-shards-storm', 'Fracture', 'blackMarket', 'shards', { count: 14, rake: 2.1, start: 0.2 }),
  decal('dc-tiger', 'Tiger', 'rare', 'tiger', { count: 9, rake: 0.28, taper: 0.7 }),
  decal('dc-tiger-fine', 'Sabretooth', 'import', 'tiger', { count: 16, rake: 0.5, taper: 0.9 }),
  decal('dc-split', 'Two-Tone', 'common', 'split', { at: 0.5, rake: 0.22, soft: 0.01, stripe: true }),
  decal('dc-split-hard', 'Hard Split', 'uncommon', 'split', { at: 0.62, rake: 0, soft: 0.002, stripe: false }),
  decal('dc-split-slash', 'Slash', 'exotic', 'split', { at: 0.45, rake: 0.8, soft: 0.05, stripe: true }),
  decal('dc-wrap', 'Lower Wrap', 'common', 'lowerWrap', { at: 0.72, dip: 0.06 }),
  decal('dc-wrap-deep', 'Deep Wrap', 'uncommon', 'lowerWrap', { at: 0.6, dip: 0.14 }),
  // Two body-specific decals. The real game has plenty, and a catalogue where
  // `bodies` is never exercised is one where the filter never gets tested.
  decal('dc-octane-crest', 'Crest', 'rare', 'split', { at: 0.4, rake: 0.45, soft: 0.02, stripe: true }, { bodies: ['octane'] }),
  decal('dc-dominus-blade', 'Bladework', 'veryRare', 'chevron', { count: 3, width: 0.1, rake: 0.2, centre: 0.66 }, { bodies: ['dominus'] }),
];

// ---------------------------------------------------------------------------
// Paint finishes
// ---------------------------------------------------------------------------

/**
 * One item per finish, and no more: `PAINT_FINISHES` takes no parameters, so a
 * second "gloss" under another name would be a different row in the garage that
 * renders identically. Five finishes that differ is better than ten that do not.
 */
const FINISHES: readonly ItemDef[] = [
  finish('pf-gloss', 'Gloss', 'common', 'gloss'),
  finish('pf-satin', 'Satin', 'common', 'satin'),
  finish('pf-matte', 'Matte', 'uncommon', 'matte'),
  finish('pf-metallic', 'Metallic', 'rare', 'metallic'),
  finish('pf-pearlescent', 'Pearlescent', 'veryRare', 'pearlescent'),
];

// ---------------------------------------------------------------------------
// Wheels
// ---------------------------------------------------------------------------

const WHEELS: readonly ItemDef[] = [
  wheels('wh-stock', 'Stock', 'common', 'rimSpoke', { spokes: 6, dish: 0.18, thickness: 0.3, lip: 1 }),
  wheels('wh-spoke5', 'Pentaspoke', 'common', 'rimSpoke', { spokes: 5, dish: 0.2, thickness: 0.34, lip: 1 }),
  wheels('wh-spoke8', 'Octaspoke', 'uncommon', 'rimSpoke', { spokes: 8, dish: 0.14, thickness: 0.24, lip: 1 }),
  wheels('wh-spoke10', 'Decaspoke', 'uncommon', 'rimSpoke', { spokes: 10, dish: 0.1, thickness: 0.2, lip: 1.05 }),
  wheels('wh-spoke3', 'Trident', 'rare', 'rimSpoke', { spokes: 3, dish: 0.26, thickness: 0.44, lip: 1.1 }),
  wheels('wh-deepdish', 'Deep Dish', 'rare', 'rimSpoke', { spokes: 6, dish: 0.4, thickness: 0.36, lip: 1.35 }),
  wheels('wh-slim', 'Featherweight', 'veryRare', 'rimSpoke', { spokes: 12, dish: 0.06, thickness: 0.14, lip: 0.85 }),
  wheels('wh-mesh', 'Meshwork', 'uncommon', 'rimMesh', { spokes: 10, dish: 0.14, lip: 1.1 }),
  wheels('wh-mesh-fine', 'Lattice', 'rare', 'rimMesh', { spokes: 20, dish: 0.1, lip: 1.2 }),
  wheels('wh-mesh-wide', 'Basketweave', 'veryRare', 'rimMesh', { spokes: 6, dish: 0.22, lip: 1.3 }),
  wheels('wh-disc', 'Discus', 'common', 'rimDish', { cone: 0.12, lip: 1.3 }),
  wheels('wh-moon', 'Moon Disc', 'uncommon', 'rimDish', { cone: 0.02, lip: 1.5 }),
  wheels('wh-cone', 'Coneflare', 'rare', 'rimDish', { cone: 0.3, lip: 1.1 }),
  wheels('wh-turbine', 'Turbine', 'rare', 'rimTurbine', { spokes: 12, dish: 0.1, rake: 0.42, lip: 0.9 }),
  wheels('wh-turbine-jet', 'Afterburner', 'veryRare', 'rimTurbine', { spokes: 20, dish: 0.06, rake: 0.8, lip: 0.8 }),
  wheels('wh-turbine-void', 'Event Horizon', 'blackMarket', 'rimTurbine', { spokes: 28, dish: 0.02, rake: 1.2, lip: 0.7 }),
  wheels('wh-split', 'Split Five', 'uncommon', 'rimSplit', { spokes: 5, dish: 0.2, spread: 0.17, lip: 1.15 }),
  wheels('wh-split-wide', 'Twin Spoke', 'rare', 'rimSplit', { spokes: 7, dish: 0.26, spread: 0.3, lip: 1.25 }),
  wheels('wh-split-tight', 'Tenspoke', 'import', 'rimSplit', { spokes: 10, dish: 0.16, spread: 0.09, lip: 1.1 }),
  wheels('wh-star', 'Starfire', 'veryRare', 'rimStar', { spokes: 5, dish: 0.28, lip: 1.25 }),
  wheels('wh-star-nova', 'Nova', 'exotic', 'rimStar', { spokes: 8, dish: 0.34, lip: 1.4 }),
];

// ---------------------------------------------------------------------------
// Boosts
// ---------------------------------------------------------------------------

/**
 * Boosts are three colours and, for the shaped ones, a length and a width. The
 * colours are what a player actually distinguishes at chase distance, which is
 * why a "Toxic" and a "Violet" are different items rather than one item with a
 * hue slider nobody would find.
 */
const BOOSTS: readonly ItemDef[] = [
  boost('bo-standard', 'Standard', 'common', 'boostFlame', { core: 0xfff3c4, hot: 0xff9d3a, cool: 0xd1400f }),
  boost('bo-cool', 'Cool Blue', 'common', 'boostFlame', { core: 0xe8f6ff, hot: 0x59b6ff, cool: 0x1140c8 }),
  boost('bo-toxic', 'Toxic', 'uncommon', 'boostFlame', { core: 0xe9ffd0, hot: 0x8de83a, cool: 0x1f7a1f }),
  boost('bo-violet', 'Violet', 'uncommon', 'boostFlame', { core: 0xf6e8ff, hot: 0xb96bff, cool: 0x5a12b0 }),
  boost('bo-crimson', 'Crimson', 'rare', 'boostFlame', { core: 0xffe0e0, hot: 0xff4a4a, cool: 0x8a0f16 }),
  boost('bo-plasma', 'Plasma', 'rare', 'boostPlasma', { core: 0xd8f4ff, hot: 0x49d2ff, cool: 0x1a3fb0, length: 1.45, width: 0.7 }),
  boost('bo-plasma-long', 'Mach', 'veryRare', 'boostPlasma', { core: 0xffffff, hot: 0x9be8ff, cool: 0x0e6fd0, length: 2.1, width: 0.5 }),
  boost('bo-plasma-wide', 'Thrust', 'rare', 'boostPlasma', { core: 0xfff2d8, hot: 0xffb638, cool: 0xb03a08, length: 1.1, width: 1.1 }),
  boost('bo-sparks', 'Sparkler', 'rare', 'boostSparks', { core: 0xfff6d0, hot: 0xffd24a, cool: 0xc06a10, length: 0.7, width: 1.15 }),
  boost('bo-sparks-gold', 'Goldrush', 'veryRare', 'boostSparks', { core: 0xfffbe0, hot: 0xffcc33, cool: 0x8a5a00, length: 0.9, width: 1.35 }),
  boost('bo-sparks-nova', 'Nova', 'blackMarket', 'boostSparks', { core: 0xffffff, hot: 0xff7ae0, cool: 0x6a0aa0, length: 1.3, width: 1.8 }),
  boost('bo-smoke', 'Smokescreen', 'uncommon', 'boostSmoke', { core: 0xd8d8d8, hot: 0x9a9a9a, cool: 0x3a3a3a, length: 0.9, width: 1.5 }),
  boost('bo-smoke-dark', 'Blackout', 'rare', 'boostSmoke', { core: 0x6a6a6a, hot: 0x3a3a3a, cool: 0x0d0d0d, length: 1.1, width: 1.9 }),
  boost('bo-smoke-floss', 'Candyfloss', 'import', 'boostSmoke', { core: 0xffffff, hot: 0xffb6e0, cool: 0xa04a80, length: 0.8, width: 1.7 }),
  boost('bo-ion', 'Ion', 'veryRare', 'boostIon', { core: 0x9ff0ff, length: 1.2, width: 0.85 }),
  boost('bo-ion-green', 'Reactor', 'import', 'boostIon', { core: 0x9dff9d, length: 1.4, width: 0.7 }),
  boost('bo-ion-void', 'Singularity', 'exotic', 'boostIon', { core: 0x2a0a4a, length: 1.6, width: 0.6 }),
  boost('bo-ion-white', 'Whiteout', 'exotic', 'boostIon', { core: 0xffffff, length: 1, width: 1 }),
];

// ---------------------------------------------------------------------------
// Toppers and antennas
// ---------------------------------------------------------------------------

const TOPPERS: readonly ItemDef[] = [
  topper('tp-cone', 'Traffic Cone', 'common', 'topperCone', { stripes: true }),
  topper('tp-cone-plain', 'Marker Cone', 'common', 'topperCone', { stripes: false }),
  topper('tp-fin', 'Shark Fin', 'uncommon', 'topperFin', { rake: 0.5 }),
  topper('tp-fin-upright', 'Dorsal', 'rare', 'topperFin', { rake: 0.1 }),
  topper('tp-dice', 'Lucky Dice', 'uncommon', 'topperDice'),
  topper('tp-beacon', 'Beacon', 'rare', 'topperBeacon'),
  topper('tp-crown-spike', 'Spiked Crown', 'rare', 'topperCrown', { points: 4 }),
  topper('tp-crown', 'Crown', 'veryRare', 'topperCrown', { points: 6 }),
  topper('tp-crown-royal', 'Royal Crown', 'import', 'topperCrown', { points: 10 }),
  topper('tp-halo', 'Halo', 'import', 'topperHalo', { tilt: 0.22 }),
  topper('tp-halo-tilt', 'Ringworld', 'exotic', 'topperHalo', { tilt: 0.9 }),
];

const ANTENNAS: readonly ItemDef[] = [
  antenna('an-ball', 'Foam Ball', 'common', 'antennaBall', { radius: 0.16 }),
  antenna('an-bead', 'Bead', 'common', 'antennaBall', { radius: 0.1 }),
  antenna('an-ball-big', 'Beach Ball', 'uncommon', 'antennaBall', { radius: 0.26 }),
  antenna('an-pennant', 'Pennant', 'common', 'antennaPennant', { chord: 0.6 }),
  antenna('an-pennant-long', 'Streamer', 'uncommon', 'antennaPennant', { chord: 1.1 }),
  antenna('an-whip', 'Whip', 'uncommon', 'antennaWhip'),
  antenna('an-star', 'Star', 'rare', 'antennaStar'),
  antenna('an-gem', 'Gem', 'veryRare', 'antennaGem'),
];

// ---------------------------------------------------------------------------
// Trails
// ---------------------------------------------------------------------------

const TRAILS: readonly ItemDef[] = [
  trail('tr-streak', 'Streak', 'common', 'trailStreak', { hot: 0xdff2ff, cool: 0x2f7fd0 }),
  trail('tr-streak-red', 'Redline', 'uncommon', 'trailStreak', { hot: 0xffd7d7, cool: 0xc01020 }),
  trail('tr-streak-green', 'Verdant', 'rare', 'trailStreak', { hot: 0xdfffd7, cool: 0x1f8a2f }),
  trail('tr-streak-violet', 'Amethyst', 'veryRare', 'trailStreak', { hot: 0xf0d7ff, cool: 0x6a10c0 }),
  trail('tr-ember', 'Ember', 'uncommon', 'trailEmber', { hot: 0xffe6b0, cool: 0xb03a08 }),
  trail('tr-ember-ash', 'Ash', 'rare', 'trailEmber', { hot: 0xd8d8d8, cool: 0x303030 }),
  trail('tr-ember-frost', 'Frostfall', 'veryRare', 'trailEmber', { hot: 0xe6faff, cool: 0x2060a0 }),
  trail('tr-ember-void', 'Voidfall', 'blackMarket', 'trailEmber', { hot: 0xc07aff, cool: 0x1a0330 }),
  trail('tr-ribbon', 'Ribbon', 'common', 'trailRibbon', { hot: 0xffffff, cool: 0x8a8a8a }),
  trail('tr-ribbon-gold', 'Gilded', 'rare', 'trailRibbon', { hot: 0xfff0b0, cool: 0xb07a10 }),
  trail('tr-ribbon-neon', 'Neon', 'import', 'trailRibbon', { hot: 0xd0ffe6, cool: 0x00b070 }),
  trail('tr-ribbon-spectrum', 'Spectrum', 'exotic', 'trailRibbon', { hot: 0xff70e0, cool: 0x30d0ff }),
];

// ---------------------------------------------------------------------------
// Goal explosions
// ---------------------------------------------------------------------------

/**
 * The first of the five slots the car renderer does not own. A goal explosion
 * fires once, in world space, from the goal mouth — src/render/effects.ts and
 * src/render/fireworks.ts are where these land — so the parameters are counts,
 * speeds and durations rather than anything about a car.
 */
const GOAL_EXPLOSIONS: readonly ItemDef[] = [
  goalExplosion('ge-burst', 'Burst', 'common', 'goalBurst', { shards: 40, speed: 900 }),
  goalExplosion('ge-burst-big', 'Big Bang', 'uncommon', 'goalBurst', { shards: 120, speed: 1600 }),
  goalExplosion('ge-supernova', 'Supernova', 'exotic', 'goalBurst', { shards: 400, speed: 3000 }),
  goalExplosion('ge-shock', 'Shockwave', 'rare', 'goalShockwave', { rings: 1, expandSpeed: 2200 }),
  goalExplosion('ge-shock-triple', 'Triple Wave', 'veryRare', 'goalShockwave', { rings: 3, expandSpeed: 2600 }),
  goalExplosion('ge-fountain', 'Fountain', 'uncommon', 'goalFountain', { jets: 6, height: 900 }),
  goalExplosion('ge-geyser', 'Geyser', 'rare', 'goalFountain', { jets: 12, height: 1600 }),
  goalExplosion('ge-vortex', 'Vortex', 'veryRare', 'goalSpiral', { arms: 4, turns: 3 }),
  goalExplosion('ge-galaxy', 'Galaxy', 'import', 'goalSpiral', { arms: 6, turns: 5 }),
  goalExplosion('ge-implode', 'Implosion', 'import', 'goalImplode', { duration: 0.9, pull: 1800 }),
  goalExplosion('ge-horizon', 'Event Horizon', 'blackMarket', 'goalImplode', { duration: 1.6, pull: 4000 }),
  goalExplosion('ge-fireworks', 'Fireworks', 'rare', 'goalFireworks', { shells: 6, sparksPerShell: 60 }),
  goalExplosion('ge-finale', 'Grand Finale', 'exotic', 'goalFireworks', { shells: 18, sparksPerShell: 140 }),
];

// ---------------------------------------------------------------------------
// Engine audio
// ---------------------------------------------------------------------------

/**
 * Synthesised, like everything else: `audioEngine` is an additive-harmonic
 * rumble whose fundamental tracks engine speed, `audioWhine` a two-oscillator
 * glide. No samples, so no sample files.
 */
const ENGINE_AUDIO: readonly ItemDef[] = [
  engineAudio('ea-stock', 'Stock', 'common', 'audioEngine', { fundamental: 90, harmonics: 6, growl: 0.3, revLimit: 7000 }),
  engineAudio('ea-v8', 'V8 Rumble', 'uncommon', 'audioEngine', { fundamental: 60, harmonics: 10, growl: 0.7, revLimit: 6000 }),
  engineAudio('ea-turbo', 'Turbo Four', 'uncommon', 'audioEngine', { fundamental: 120, harmonics: 5, growl: 0.4, revLimit: 8000 }),
  engineAudio('ea-diesel', 'Diesel', 'rare', 'audioEngine', { fundamental: 45, harmonics: 12, growl: 0.9, revLimit: 4500 }),
  engineAudio('ea-rotary', 'Rotary', 'veryRare', 'audioEngine', { fundamental: 150, harmonics: 8, growl: 0.5, revLimit: 9000 }),
  engineAudio('ea-electric', 'Electric', 'rare', 'audioWhine', { fundamental: 400, harmonics: 2, glide: 1 }),
  engineAudio('ea-jet', 'Jet Turbine', 'rare', 'audioWhine', { fundamental: 900, harmonics: 3, glide: 0.6 }),
  engineAudio('ea-null', 'Null', 'exotic', 'audioWhine', { fundamental: 60, harmonics: 1, glide: 0 }),
];

// ---------------------------------------------------------------------------
// Banners, titles, avatar borders
// ---------------------------------------------------------------------------

const BANNERS: readonly ItemDef[] = [
  banner('bn-plain', 'Plain', 'common', 'bannerGradient', { stops: 2, angle: 0, noise: 0 }),
  banner('bn-diagonal', 'Diagonal', 'common', 'bannerStripes', { stripes: 6, angle: 30 }),
  banner('bn-chequer', 'Chequer', 'uncommon', 'bannerStripes', { stripes: 12, angle: 90 }),
  banner('bn-grid', 'Grid', 'uncommon', 'bannerGrid', { cells: 20 }),
  banner('bn-sunburst', 'Sunburst', 'rare', 'bannerBurst', { rays: 16, glow: 0, inverted: false }),
  banner('bn-flare', 'Flare', 'veryRare', 'bannerBurst', { rays: 32, glow: 1, inverted: false }),
  banner('bn-nebula', 'Nebula', 'import', 'bannerGradient', { stops: 5, angle: 135, noise: 0.4 }),
  banner('bn-eclipse', 'Eclipse', 'exotic', 'bannerBurst', { rays: 64, glow: 1, inverted: true }),
];

/** `titleText` draws the string, and the string IS the item. */
const TITLES: readonly ItemDef[] = [
  title('ti-rookie', 'Rookie', 'common', 'titleText', { text: 'Rookie', color: 0xb9c0c8, glow: 0 }),
  title('ti-driver', 'Driver', 'common', 'titleText', { text: 'Driver', color: 0xb9c0c8, glow: 0 }),
  title('ti-striker', 'Striker', 'uncommon', 'titleText', { text: 'Striker', color: 0x4f9cf0, glow: 0 }),
  title('ti-aerialist', 'Aerialist', 'uncommon', 'titleText', { text: 'Aerialist', color: 0x4f9cf0, glow: 0 }),
  title('ti-wallrider', 'Wall Rider', 'rare', 'titleText', { text: 'Wall Rider', color: 0x2f6fe0, glow: 0 }),
  title('ti-keeper', 'Goalkeeper', 'rare', 'titleText', { text: 'Goalkeeper', color: 0x2f6fe0, glow: 0 }),
  title('ti-demolitionist', 'Demolitionist', 'rare', 'titleText', { text: 'Demolitionist', color: 0x2f6fe0, glow: 0 }),
  title('ti-champion', 'Champion', 'veryRare', 'titleText', { text: 'Champion', color: 0x8b5cf6, glow: 0.4 }),
  title('ti-grand-champion', 'Grand Champion', 'import', 'titleText', { text: 'Grand Champion', color: 0xe0417b, glow: 0.6 }),
  title('ti-ssl', 'Supersonic Legend', 'exotic', 'titleText', { text: 'Supersonic Legend', color: 0xf2c14e, glow: 1 }),
  title('ti-founder', 'Founder', 'limited', 'titleText', { text: 'Founder', color: 0xff8c1a, glow: 1 }, { exclusive: true }),
  title('ti-season-one', 'Season One', 'limited', 'titleText', { text: 'Season One', color: 0xff8c1a, glow: 1 }, { exclusive: true }),
];

const AVATAR_BORDERS: readonly ItemDef[] = [
  avatarBorder('ab-plain', 'Plain', 'common', 'borderRing', { thickness: 0.06, segments: 0, glow: 0, inner: false }),
  avatarBorder('ab-dashed', 'Dashed', 'common', 'borderRing', { thickness: 0.06, segments: 12, glow: 0, inner: false }),
  avatarBorder('ab-double', 'Double', 'uncommon', 'borderRing', { thickness: 0.1, segments: 0, glow: 0, inner: true }),
  avatarBorder('ab-corona', 'Corona', 'veryRare', 'borderRing', { thickness: 0.08, segments: 36, glow: 1, inner: false }),
  avatarBorder('ab-ember', 'Ember Ring', 'exotic', 'borderRing', { thickness: 0.12, segments: 0, glow: 2, inner: true }),
  avatarBorder('ab-laurel', 'Laurel', 'rare', 'borderLaurel', { leaves: 14, glow: 0 }),
  avatarBorder('ab-laurel-gold', 'Golden Laurel', 'import', 'borderLaurel', { leaves: 20, glow: 1 }),
  avatarBorder('ab-founder', "Founder's Wreath", 'limited', 'borderLaurel', { leaves: 24, glow: 2 }, { exclusive: true }),
];

// ---------------------------------------------------------------------------
// [ours] Numbers 0-99
// ---------------------------------------------------------------------------

/**
 * A hundred items from three generators and one parameter.
 *
 * The rarity rule is motorsport rather than arbitrary: single digits are the
 * prized ones, repdigits (11, 22, ... 99) the next most wanted, and the other
 * eighty-one common. Being a RULE rather than a table means it cannot drift,
 * and it is what makes 0-9 certifiable while 34 is only paintable.
 *
 * The letterform follows the rarity for the same reason it does on a real car:
 * the numbers people fight over are the ones that get the roundel.
 */
export const numberRarity = (value: number): Rarity =>
  value < 10 ? 'rare' : value % 11 === 0 ? 'uncommon' : 'common';

export const numberGenerator = (value: number): string =>
  value < 10 ? 'numberRoundel' : value % 11 === 0 ? 'numberOutline' : 'numberPlain';

const NUMBERS: readonly ItemDef[] = Array.from({ length: 100 }, (_, value) =>
  def(
    'number',
    `num-${String(value).padStart(2, '0')}`,
    `#${String(value).padStart(2, '0')}`,
    numberRarity(value),
    numberGenerator(value),
    // `digits` is what the renderer reads first; `value` is the same thing as a
    // number, kept because a filter or a sort wants an integer, not '07'.
    { value, digits: String(value).padStart(2, '0') }
  )
);

// ---------------------------------------------------------------------------
// [ours] Country flags
// ---------------------------------------------------------------------------

/**
 * Thirty-one flags, chosen for one reason: they are exactly drawable from
 * bands, crosses, saltires, discs and triangles. Nothing here is an
 * approximation of a coat of arms.
 *
 * The flags that are NOT here are not missing by accident — see
 * docs/COSMETICS_SOURCES.md, which lists the exclusions with a reason for each.
 * Anything needing an emblem (Spain, Portugal, Mexico, Serbia), a ring or field
 * of stars (USA, EU, China, Australia, Brazil) or a device (Canada's leaf,
 * India's chakra, South Korea's trigrams) is out, because a smeared
 * approximation is wrong about a real country at a size where nobody can tell
 * it was meant to be something else. The Union Flag IS here: it is pure
 * geometry, counterchange included, and src/render/decals.ts draws it properly.
 *
 * Colours are the commonly published specifications, converted to 0xRRGGBB.
 */
const flagItem = (
  id: string,
  name: string,
  rarity: Rarity,
  generator: string,
  params: Params = {}
): ItemDef => def('flag', `flag-${id}`, name, rarity, generator, params);

/** Three equal vertical bands: hoist, middle, fly. */
const vertical = (id: string, name: string, a: number, b: number, c: number): ItemDef =>
  flagItem(id, name, 'common', 'flagBands', { count: 3, vertical: true, a, b, c });

/** Three equal horizontal bands: top, middle, bottom. */
const horizontal = (id: string, name: string, a: number, b: number, c: number): ItemDef =>
  flagItem(id, name, 'common', 'flagBands', { count: 3, vertical: false, a, b, c });

/** Two equal horizontal bands. */
const halved = (id: string, name: string, a: number, b: number): ItemDef =>
  flagItem(id, name, 'common', 'flagBands', { count: 2, vertical: false, a, b });

const FLAGS: readonly ItemDef[] = [
  vertical('fr', 'France', 0x0055a4, 0xffffff, 0xef4135),
  vertical('it', 'Italy', 0x008c45, 0xf4f5f0, 0xcd212a),
  vertical('ie', 'Ireland', 0x169b62, 0xffffff, 0xff883e),
  vertical('be', 'Belgium', 0x000000, 0xfdda24, 0xef3340),
  vertical('ro', 'Romania', 0x002b7f, 0xfcd116, 0xce1126),
  vertical('ng', 'Nigeria', 0x008751, 0xffffff, 0x008751),
  horizontal('nl', 'Netherlands', 0xae1c28, 0xffffff, 0x21468b),
  horizontal('de', 'Germany', 0x000000, 0xdd0000, 0xffce00),
  horizontal('at', 'Austria', 0xed2939, 0xffffff, 0xed2939),
  horizontal('hu', 'Hungary', 0xcd2a3e, 0xffffff, 0x436f4d),
  horizontal('bg', 'Bulgaria', 0xffffff, 0x00966e, 0xd62612),
  horizontal('ru', 'Russia', 0xffffff, 0x0039a6, 0xd52b1e),
  horizontal('ee', 'Estonia', 0x0072ce, 0x000000, 0xffffff),
  horizontal('lt', 'Lithuania', 0xfdb913, 0x006a44, 0xc1272d),
  // The one unequal tricolour: Colombia's top band is half the hoist, which is
  // exactly what `first` is for.
  flagItem('co', 'Colombia', 'uncommon', 'flagBands', {
    count: 3, vertical: false, a: 0xfcd116, b: 0x003893, c: 0xce1126, first: 0.5,
  }),
  halved('pl', 'Poland', 0xffffff, 0xdc143c),
  halved('ua', 'Ukraine', 0x0057b7, 0xffd700),
  halved('id', 'Indonesia', 0xff0000, 0xffffff),
  // Nordic crosses. `inner` is the second, narrower cross Norway and Iceland
  // carry; -1 means the flag has none.
  flagItem('dk', 'Denmark', 'uncommon', 'flagNordicCross', { field: 0xc8102e, cross: 0xffffff, inner: -1 }),
  flagItem('se', 'Sweden', 'uncommon', 'flagNordicCross', { field: 0x006aa7, cross: 0xfecc02, inner: -1 }),
  flagItem('fi', 'Finland', 'uncommon', 'flagNordicCross', { field: 0xffffff, cross: 0x003580, inner: -1 }),
  flagItem('no', 'Norway', 'uncommon', 'flagNordicCross', { field: 0xba0c2f, cross: 0xffffff, inner: 0x00205b }),
  flagItem('is', 'Iceland', 'uncommon', 'flagNordicCross', { field: 0x02529c, cross: 0xffffff, inner: 0xdc1e35 }),
  // Centred crosses. Switzerland's arms stop short of the edge, which is the
  // whole difference between it and England.
  flagItem('ch', 'Switzerland', 'uncommon', 'flagCross', { field: 0xd52b1e, cross: 0xffffff, width: 0.22, reach: 0.66 }),
  flagItem('gb-eng', 'England', 'uncommon', 'flagCross', { field: 0xffffff, cross: 0xce1124, width: 0.2, reach: 1 }),
  flagItem('gb-sct', 'Scotland', 'uncommon', 'flagSaltire', { field: 0x0065bf, cross: 0xffffff, width: 0.22 }),
  // Discs. Bangladesh's and Palau's are offset toward the hoist so they look
  // centred when the flag flies.
  flagItem('jp', 'Japan', 'uncommon', 'flagDisc', { field: 0xffffff, disc: 0xbc002d, radius: 0.3 }),
  flagItem('bd', 'Bangladesh', 'uncommon', 'flagDisc', { field: 0x006a4e, disc: 0xf42a41, radius: 0.3, offsetX: -0.05 }),
  flagItem('pw', 'Palau', 'uncommon', 'flagDisc', { field: 0x4aadd6, disc: 0xffde00, radius: 0.3, offsetX: -0.06 }),
  flagItem('cz', 'Czechia', 'rare', 'flagHoistTriangle', { a: 0xffffff, b: 0xd7141a, triangle: 0x11457e, reach: 0.5 }),
  flagItem('gb', 'United Kingdom', 'rare', 'flagUnionJack'),
];

// ---------------------------------------------------------------------------
// The catalogue
// ---------------------------------------------------------------------------

export const ITEMS: readonly ItemDef[] = Object.freeze([
  ...BODIES,
  ...DECALS,
  ...FINISHES,
  ...WHEELS,
  ...BOOSTS,
  ...TOPPERS,
  ...ANTENNAS,
  ...TRAILS,
  ...GOAL_EXPLOSIONS,
  ...ENGINE_AUDIO,
  ...BANNERS,
  ...TITLES,
  ...AVATAR_BORDERS,
  ...NUMBERS,
  ...FLAGS,
]);

const BY_ID: ReadonlyMap<string, ItemDef> = new Map(ITEMS.map(i => [i.id, i]));

/** `undefined` for an unknown id — a stored inventory can outlive an item. */
export const itemById = (id: string): ItemDef | undefined => BY_ID.get(id);

const BY_SLOT: ReadonlyMap<CosmeticSlot, readonly ItemDef[]> = (() => {
  const out = new Map<CosmeticSlot, ItemDef[]>();
  for (const item of ITEMS) {
    const list = out.get(item.slot);
    if (list) list.push(item);
    else out.set(item.slot, [item]);
  }
  return out;
})();

const EMPTY: readonly ItemDef[] = Object.freeze([]);

/**
 * Every item for a slot, optionally filtered to the ones a given body can wear.
 * An item with no `bodies` is universal, which is the overwhelming majority —
 * only two decals are restricted, and each `body` item is restricted to itself.
 */
export const itemsForSlot = (slot: CosmeticSlot, body?: CarBody): readonly ItemDef[] => {
  const all = BY_SLOT.get(slot) ?? EMPTY;
  if (body === undefined) return all;
  return all.filter(i => i.bodies === undefined || i.bodies.includes(body));
};

/** Every item at a rarity. Used by the drop tables and the garage filters. */
export const itemsOfRarity = (rarity: Rarity): readonly ItemDef[] =>
  ITEMS.filter(i => i.rarity === rarity);

/** The bodies a player can actually select. See `RENDERED_BODIES`. */
export const SELECTABLE_BODIES: readonly CarBody[] = Object.freeze([...RENDERED_BODIES]);

/** Every distinct generator the catalogue names, sorted. */
export const GENERATORS: readonly string[] = Object.freeze(
  [...new Set(ITEMS.map(i => i.generator))].sort()
);

/** Catalogue size per slot, for the garage's tab counts and for tests. */
export const slotCounts = (): Readonly<Record<string, number>> => {
  const out: Record<string, number> = {};
  for (const item of ITEMS) out[item.slot] = (out[item.slot] ?? 0) + 1;
  return out;
};

/** Catalogue size per rarity. */
export const rarityCounts = (): Readonly<Record<string, number>> => {
  const out: Record<string, number> = {};
  for (const r of RARITY_ORDER) out[r] = 0;
  for (const item of ITEMS) out[item.rarity] = (out[item.rarity] ?? 0) + 1;
  return out;
};

/** '#rrggbb' for a painted variant, which is the form `Loadout` stores. */
export const paintHexString = (paint: PaintColor): string =>
  `#${PAINT_HEX[paint].toString(16).padStart(6, '0')}`;

/** Every paint colour with its hex, in ladder order — the garage swatch strip. */
export const PAINT_SWATCHES: readonly { paint: PaintColor; hex: number }[] = Object.freeze(
  PAINT_COLORS.map(paint => ({ paint, hex: PAINT_HEX[paint] }))
);
